import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, articles } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-800 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabelMap: Record<string, string> = {
    anatomy: '解剖结构',
    physiology: '生理功能',
    diseases: '常见疾病',
    wellness: '健康保养',
    examination: '检查指南',
  };

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readdy.ai/' },
      { '@type': 'ListItem', position: 2, name: categoryLabelMap[article.category] || article.category, item: `https://readdy.ai/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishedDate,
    image: article.imageUrl,
    url: `https://readdy.ai/article/${article.slug}`,
    inLanguage: 'zh-CN',
    articleSection: categoryLabelMap[article.category],
    keywords: article.tags.join(', '),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdArticle) }} />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryLabelMap[article.category] || article.category, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-3xl mx-auto">
            <div className="relative w-full h-56 md:h-72 rounded-lg overflow-hidden mb-6">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-2.5 py-0.5 text-xs font-medium bg-primary-50 text-primary-600 rounded-full cursor-pointer"
                >
                  {categoryLabelMap[article.category]}
                </Link>
                <span className="text-xs text-foreground-400">{article.author}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{article.publishedDate}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400 flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 text-xs bg-background-100 text-foreground-500 rounded">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div
              className="prose-custom text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <p className="text-xs text-foreground-400 mb-1">
                <strong>免责声明：</strong>本站内容仅供参考，不构成医疗诊断或治疗建议。如有健康问题，请及时咨询专业医生。
              </p>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-heading text-xl font-bold text-foreground-800 mb-6">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.slug} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}